import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CustomToolbar,
  CustomToolbarAction,
} from '@work4all/components/lib/dataDisplay/actions-bar/ToolbarTypes';

import { TimeTracking } from '@work4all/models/lib/Classes/TimeTracking.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { SectionName } from '@work4all/utils/lib/mark-sections-by-date';

import {
  useOpenTimeTrackingMask,
  useTimeTrackingMaskActions,
} from '../../time-tracking-mask/hooks';
import { EntityTableV8, IEntityTableV8 } from '../entity-table/EntityTableV8';
import { useEntityDataTableV8 } from '../entity-table/use-entity-data-table-v8';
import schema from '../schemata/ticket-time-tracking-table-schema.json';

const defaultSort = [{ field: 'date', direction: SortDirection.DESCENDING }];

const AVAILABLE_DATE_SECTIONS: SectionName[] = [
  'future',
  'today',
  'yesterday',
  'lastWeek',
  'thisMonth',
  'thisYear',
  'lastYear',
  'before',
  'unknown',
];

export function TicketTimeTrackingTable(props: IEntityTableV8) {
  const dataTable = useEntityDataTableV8<TimeTracking, never>({
    ...props,
    schema: schema as never,
    defaultSort,
    availableDateSections: AVAILABLE_DATE_SECTIONS,
    markWithDayNameForPastDaysThisWeek: true,
    listEntityType: Entities.timeTracking,
    enableURLState: false,
  });

  const initialData = useMemo(() => {
    const presetFields = props.maskParams?.presetFields;

    if (!presetFields) return null;

    const parsedPresetFields = JSON.parse(presetFields);

    return {
      ...parsedPresetFields,
      startDateTime: getDate(parsedPresetFields.startDateTime),
      endDateTime: getDate(parsedPresetFields.endDateTime),
    };
  }, [props.maskParams?.presetFields]);

  const actions = useTimeTrackingMaskActions(
    dataTable.selectedEntities?.[0],
    initialData
  );

  const openTimeTrackingMask = useOpenTimeTrackingMask();
  const { t } = useTranslation();
  const allActions = useMemo(() => {
    const customActions: CustomToolbarAction[] = [
      {
        actionKey: 'custom',
        IconComponent: ContentCopyIcon,
        tooltip: t('COMMON.CLONE'),
        handler: () => {
          openTimeTrackingMask({
            id: dataTable.selectedEntities?.[0]?.id,
            mode: 'clone',
          });
        },
        mode: 'Edit',
      },
    ];
    const customToolbar: CustomToolbar = {
      left: customActions,
    };
    return {
      ...actions,
      custom: customToolbar,
    };
  }, [actions, t, openTimeTrackingMask]);

  return (
    <EntityTableV8
      {...props}
      {...dataTable}
      actions={allActions}
      hideSearch
      hideMoreButton
      hideRightActions
      overrides={{ areas: null }}
      displayFooter
    />
  );
}

const getDate = (date: string) => {
  if (!date) return null;

  return DateTime.fromISO(date).toJSDate();
};
